import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootReducerState } from '../../redux/Reducers';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { PaginationType } from '../../types/utils/pagination.type';
import {
  CreateULAREAllowDto,
  CreateULARECentralStationDto,
  CreateULARELineDto,
  DeleteULAREAllowDto,
  DeleteULARELineDto,
  HeartbeatChangeType,
  HeartbeatStatus,
  ULARECentralStationType,
  ULARELine,
  UpdateULARECentralStationDto,
} from '../../types/ulare/ulare.types';
import { CentralStationsRouteParams } from './ulare.slice';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { AlarmType } from '../../types/alarm/alarm.type';
import { RouteParamsType } from '../../types/utils/router-params.type';

export const postCentralStation = createAsyncThunk<
  { csId: string },
  { data: CreateULARECentralStationDto },
  { state: RootReducerState }
>('ulare/postCentralStation', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ csId: string }>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating central station!' });
  }
});

export const postLine = createAsyncThunk<
  ULARELine,
  { data: CreateULARELineDto; csId: string },
  { state: RootReducerState }
>('ulare/postLine', async ({ data, csId }, thunkAPI) => {
  try {
    const response = await axios.post<ULARELine>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${csId}/line`,
      data
    );

    thunkAPI.dispatch(getLineIDs({ id: csId }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating line ID!' });
  }
});

export const postAllow = createAsyncThunk<
  { ipAddress: string },
  { data: CreateULAREAllowDto; csId: string },
  { state: RootReducerState }
>('ulare/postAllow', async ({ data, csId }, thunkAPI) => {
  try {
    const response = await axios.post<{ ipAddress: string }>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${csId}/allow`,
      data
    );

    thunkAPI.dispatch(getAllowList({ id: csId }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating allow address!' });
  }
});

export const getHeartbeatStatus = createAsyncThunk<HeartbeatStatus, void, { state: RootReducerState }>(
  'ulare/getHeartbeatStatus',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<{ status: HeartbeatStatus }>(
        `${process.env.REACT_APP_API_URL}/contact-id/ulare/heartbeat-status`
      );
      return response.data.status;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching heartbeat status!' });
    }
  }
);

export const getHeartbeatChanges = createAsyncThunk<
  PaginationType<HeartbeatChangeType>,
  { limit: number; page: number },
  { state: RootReducerState }
>('ulare/getHeartbeatChanges', async ({ page, limit }, thunkAPI) => {
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('page', String(page));

    const response = await axios.get<PaginationType<HeartbeatChangeType>>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/heartbeat`,
      { params }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching heartbeat changes!' });
  }
});

export const getCentralStation = createAsyncThunk<ULARECentralStationType, { id: string }, { state: RootReducerState }>(
  'ulare/getCentralStation',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<ULARECentralStationType>(
        `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching central station!' });
    }
  }
);

export const getConnection = createAsyncThunk<
  { ipAddress: string; connectedAt: null | Date },
  { id: string },
  { state: RootReducerState }
>('ulare/getConnection', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<{ ipAddress: string; connectedAt: null | Date }>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${id}/connection`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching central station connection!' });
  }
});

export const resetConnection = createAsyncThunk<void, { id: string }, { state: RootReducerState }>(
  'ulare/resetConnection',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<void>(
        `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${id}/connection`
      );

      thunkAPI.dispatch(getConnection({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during resetting central station connection!' });
    }
  }
);

export const getAllowList = createAsyncThunk<string[], { id: string }, { state: RootReducerState }>(
  'ulare/getAllowList',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<string[]>(
        `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${id}/allow-list`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching allow list!' });
    }
  }
);

export const getLineIDs = createAsyncThunk<ULARELine[], { id: string }, { state: RootReducerState }>(
  'ulare/getLineIDs',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<ULARELine[]>(
        `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${id}/line-ids`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching line IDs!' });
    }
  }
);

export const getCentralStationsList = createAsyncThunk<
  PaginationType<ULARECentralStationType>,
  CentralStationsRouteParams & { controller?: AbortController },
  { state: RootReducerState }
>('ulare/getCentralStationsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      undefined,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      undefined
    );

    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<ULARECentralStationType>>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data || [];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching central stations!' });
  }
});

export const patchCentralStation = createAsyncThunk<
  ULARECentralStationType,
  { id: string; data: UpdateULARECentralStationDto },
  { state: RootReducerState }
>('ulare/patchCentralStation', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<ULARECentralStationType>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating central station!' });
  }
});

export const cancelAlarm = createAsyncThunk<{ success: boolean }, { id: string }, { state: RootReducerState }>(
  'ulare/cancelAlarm',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.patch<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/contact-id/alarms/${id}/cancel`
      );

      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during canceling alarm!' });
    }
  }
);

export const deleteLine = createAsyncThunk<
  { csId: string },
  { data: DeleteULARELineDto; csId: string },
  { state: RootReducerState }
>('ulare/deleteLine', async ({ data, csId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ csId: string }>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${csId}/line`,
      { data }
    );

    thunkAPI.dispatch(getLineIDs({ id: csId }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting line ID!' });
  }
});

export const deleteCentralStation = createAsyncThunk<
  { success: boolean },
  { csId: string; fetchList: boolean },
  { state: RootReducerState }
>('ulare/deleteCentralStation', async ({ csId, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${csId}`
    );

    if (fetchList) {
      thunkAPI.dispatch(getCentralStationsList(thunkAPI.getState().ulareReducer.centralStationsRouteParams));
    }

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting line ID!' });
  }
});

export const deleteAllow = createAsyncThunk<
  { message: string },
  { data: DeleteULAREAllowDto; csId: string },
  { state: RootReducerState }
>('ulare/deleteAllow', async ({ data, csId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ message: string }>(
      `${process.env.REACT_APP_API_URL}/contact-id/ulare/central-station/${csId}/allow`,
      { data }
    );

    thunkAPI.dispatch(getAllowList({ id: csId }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting address!' });
  }
});

export const getULAREAlarmsList = createAsyncThunk<
  PaginationType<AlarmType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('ulare/getULAREAlarmsList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      undefined,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      undefined
    );

    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<AlarmType>>(
      `${process.env.REACT_APP_API_URL}/contact-id/alarms${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching alarms!' });
  }
});
